import {
  Breadcrumb,
  Button,
  Divider,
  Input,
  Layout,
  notification,
  Result,
  Skeleton,
  theme
} from "antd";
import { Content } from "antd/es/layout/layout";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiPointOfSaleDetail, apiPointOfSaleUpdate } from "../../../../api/pointOfSale";
import AccountsSearchSelect from "../../../common/AccountsSearchSelect";
import "./PointOfSaleDetails.scss";
import {EyeOutlined, EditOutlined, SaveOutlined, CloseOutlined} from "@ant-design/icons";

const PointOfSaleDetails = () => {
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const navigate = useNavigate();
  const { id } = useParams();

  const [pointOfSaleData, setPointOfSaleData] = useState();
  const [loading, setLoading] = useState(true);
  const [activeError, setActiveError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedReceiverAccount, setSelectedReceiverAccount] = useState(null);
  const [saving, setSaving] = useState(false);

  const renderError = (status) => {
    if (activeError) {
      return (
        <Result
          status={status === "500" ? "500" : "404"}
          title={status}
          subTitle={
            status === "500"
              ? "Ocorreu um erro no servidor, tente novamente mais tarde."
              : "Ocorreu um erro, tente novamente mais tarde."
          }
          extra={
            <Button type="primary" onClick={() => navigate(-1)}>
              Voltar
            </Button>
          }
        />
      );
    }
  };

  const renderStatusName = (status) => {
    const statusMap = {
      active: "Ativo",
      inactive: "Inativo",
      pending: "Pendente",
      blocked: "Bloqueado"
    };

    return statusMap[status] || "Desconhecido";
  }

  const renderStatus = (status) => {
    const statusMap = {
      active: { label: "Ativo", class: "badge-success" },
      inactive: { label: "Inativo", class: "badge-error" },
      pending: { label: "Pendente", class: "orange" },
      blocked: { label: "Bloqueado", class: "badge-error" }
    };

    const statusInfo = statusMap[status] || { label: status, class: "badge-error" };
    
    return (
      <span className={`badge ${statusInfo.class}`}>
        {statusInfo.label}
      </span>
    );
  };

  useEffect(() => {
    const fetchPointOfSaleDetail = async () => {
      try {
        setLoading(true);
        const data = await apiPointOfSaleDetail(id);
        setPointOfSaleData(data);
      } catch (error) {
        setActiveError(true);
        if (error.status === 404) {
          setErrorStatus("404");
          notification.error({
            message: "Erro",
            description: "Terminal POS não encontrado.",
            placement: "topRight",
          });
        } else if (error.status === 500) {
          setErrorStatus("500");
          notification.error({
            message: "Erro",
            description: "Erro interno do servidor. Tente novamente mais tarde.",
            placement: "topRight",
          });
        } else {
          setErrorStatus("500");
          notification.error({
            message: "Erro",
            description: "Erro ao carregar os detalhes do terminal POS.",
            placement: "topRight",
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPointOfSaleDetail();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd/MM/yyyy 'às' HH:mm:ss");
    } catch (error) {
      return "Data inválida";
    }
  };

  const handleEditClick = () => {
    setIsEditMode(true);
    setSelectedReceiverAccount(pointOfSaleData?.receiver_account || null);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setSelectedReceiverAccount(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiPointOfSaleUpdate(id, {
        receiver_account: selectedReceiverAccount?.id || null
      });

      notification.success({
        message: "Sucesso",
        description: "Conta recebedora atualizada com sucesso.",
        placement: "topRight",
      });

      // Refresh data
      const data = await apiPointOfSaleDetail(id);
      setPointOfSaleData(data);
      setIsEditMode(false);
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Erro ao atualizar conta recebedora.",
        placement: "topRight",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="PointOfSaleDetails">
      <Layout
        style={{
          padding: "1.25rem",
        }}
      >
        {activeError ? (
          renderError(errorStatus)
        ) : (
          <>
            <Breadcrumb
              style={{
                margin: "16px 0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                }}
              >
                {loading ? (
                  <Skeleton active paragraph={{ rows: 1 }} />
                ) : (
                  <h2 className="title-page">
                    {pointOfSaleData?.identification_number || "Terminal POS"}
                  </h2>
                )}
                {!loading && (
                  !isEditMode ? (
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={handleEditClick}
                    >
                      Editar
                    </Button>
                  ) : (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        loading={saving}
                        onClick={handleSave}
                      >
                        Salvar
                      </Button>
                      <Button
                        icon={<CloseOutlined />}
                        onClick={handleCancelEdit}
                        disabled={saving}
                      >
                        Cancelar
                      </Button>
                    </div>
                  )
                )}
              </div>
              <Divider />
              {loading ? (
                <Skeleton active paragraph={{ rows: 0 }} />
              ) : (
                <>
                  <Breadcrumb.Item 
                    className="goBack" 
                    onClick={() => navigate(-1)}
                  >
                    Terminais POS
                  </Breadcrumb.Item>
                  <Breadcrumb.Item>
                    {pointOfSaleData?.identification_number}
                  </Breadcrumb.Item>
                </>
              )}
            </Breadcrumb>

            {/* Terminal Information */}
            <Content
              style={{
                padding: 24,
                margin: 0,
                minHeight: 280,
                background: colorBgContainer,
                borderRadius: borderRadiusLG,
              }}
            >
              {loading ? (
                <Skeleton active paragraph={{ rows: 1 }} />
              ) : (
                <div className="container-subtitle">
                  <h3 className="subtitle-page">Informações do Terminal</h3>
                  {renderStatus(pointOfSaleData?.status)}
                </div>
              )}
              <Divider />
              {loading ? (
                <Skeleton active paragraph={{ rows: 6 }} />
              ) : (
                <div className="container-inputs">
                  <div className="container-left">
                    <div>
                      <p>Número de Série</p>
                      <Input 
                        disabled 
                        value={pointOfSaleData?.serial_number || "N/A"} 
                      />
                    </div>
                    <div>
                      <p>Fabricante</p>
                      <Input 
                        disabled 
                        value={pointOfSaleData?.manufacturer || "N/A"} 
                      />
                    </div>
                    <div>
                      <p>Modelo</p>
                      <Input 
                        disabled 
                        value={pointOfSaleData?.terminal_model || "N/A"} 
                      />
                    </div>
                    <div>
                      <p>Data de Criação</p>
                      <Input 
                        disabled 
                        value={formatDate(pointOfSaleData?.created_at)} 
                      />
                    </div>
                    <div>
                      <p>Identificador Adquirente</p>
                      <Input 
                        disabled 
                        value={pointOfSaleData?.zoop_id} 
                      />
                    </div>
                  </div>
                  <div className="container-right">
                    <div>
                      <p>Conta Adquirente</p>
                      <div className='input-container'>
                          <Input disabled value={pointOfSaleData?.acquirer_account?.display_name || "N/A"} />
                          <EyeOutlined
                            onClick={() => window.open(`/lojista/detalhes/${pointOfSaleData?.acquirer_account?.id}`, "_blank")}
                            className='eye-icon'
                          />
                      </div>
                    </div>
                    <div>
                      <p>Conta Recebedora</p>
                      {isEditMode ? (
                        <AccountsSearchSelect
                          value={selectedReceiverAccount}
                          onChange={setSelectedReceiverAccount}
                          placeholder="Selecione uma conta recebedora"
                          disabled={false}
                          notFoundText="Nenhuma conta encontrada"
                          showEyeIcon={true}
                          eyeIconUrl="/lojista/detalhes/{id}"
                        />
                      ) : (
                        <div className='input-container'>
                          <Input disabled value={pointOfSaleData?.receiver_account?.display_name || "N/A"} />
                          {pointOfSaleData?.receiver_account?.id && (
                            <EyeOutlined
                              onClick={() => window.open(`/lojista/detalhes/${pointOfSaleData?.receiver_account?.id}`, "_blank")}
                              className='eye-icon'
                            />
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <p>Última transação em</p>
                      <Input
                        disabled
                        value={formatDate(pointOfSaleData?.last_tx_at)}
                      />
                    </div>
                    <div>
                      <p>Status</p>
                      <Input 
                        disabled 
                        value={renderStatusName(pointOfSaleData?.status)} 
                      />
                    </div>
                    <div>
                      <p>Última Atualização</p>
                      <Input 
                        disabled 
                        value={formatDate(pointOfSaleData?.updated_at)} 
                      />
                    </div>
                  </div>
                </div>
              )}
            </Content>
          </>
        )}
      </Layout>
    </div>
  );
};

export default PointOfSaleDetails;
